import { createStitches } from "@stitches/react";
import { slate, red, cyan, cyanA, green, orange } from "@radix-ui/colors";

const rem = (px: number) => `${px / 16}rem`;

export const { styled, config, css, globalCss, keyframes, theme } =
  createStitches({
    theme: {
      colors: {
        ...slate,
        ...red,
        ...green,
        ...orange,
        ...cyan,
        ...cyanA,

        background: "#fff",
        backgroundTranslucent: "rgba(255, 255, 255, 0.75)",
        surface: "#fff",
        backdrop: "rgba(0, 0, 0, 0.35)",

        neutral1: "$slate1",
        neutral2: "$slate2",
        neutral3: "$slate3",
        neutral4: "$slate4",
        neutral5: "$slate5",
        neutral6: "$slate6",
        neutral7: "$slate7",
        neutral8: "$slate8",
        neutral9: "$slate9",
        neutral10: "$slate10",
        neutral11: "$slate11",
        neutral12: "$slate12",

        primary1: "$cyan1",
        primary2: "$cyan2",
        primary3: "$cyan3",
        primary4: "$cyan4",
        primary5: "$cyan5",
        primary6: "$cyan6",
        primary7: "$cyan7",
        primary8: "$cyan8",
        primary9: "$cyan9",
        primary10: "$cyan10",
        primary11: "$cyan11",
        primary12: "$cyan12",

        danger1: "$red1",
        danger2: "$red2",
        danger3: "$red3",
        danger4: "$red4",
        danger5: "$red5",
        danger6: "$red6",
        danger7: "$red7",
        danger8: "$red8",
        danger9: "$red9",
        danger10: "$red10",
        danger11: "$red11",
        danger12: "$red12",

        neutralBase: "$neutral1",
        neutralBgSubtle: "$neutral2",
        neutralBg: "$neutral3",
        neutralBgHover: "$neutral4",
        neutralBgActive: "$neutral5",
        neutralLine: "$neutral6",
        neutralBorder: "$neutral7",
        neutralBorderHover: "$neutral8",
        neutralBgSolid: "$neutral9",
        neutralBgSolidHover: "$neutral10",
        neutralText: "$neutral11",
        neutralTextContrast: "$neutral12",
        onNeutralDark: "#fff",

        primaryBase: "$primary1",
        primaryBgSubtle: "$primary2",
        primaryBg: "$primary3",
        primaryBgHover: "$primary4",
        primaryBgActive: "$primary5",
        primaryLine: "$primary6",
        primaryBorder: "$primary7",
        primaryBorderHover: "$primary8",
        primaryBgSolid: "$primary9",
        primaryBgSolidHover: "$primary10",
        primaryText: "$primary11",
        primaryTextContrast: "$primary12",
        onPrimary: "#fff",

        errorBase: "$red1",
        errorBgSubtle: "$red2",
        errorBg: "$red3",
        errorBgHover: "$red4",
        errorBgActive: "$red5",
        errorLine: "$red6",
        errorBorder: "$red7",
        errorBorderHover: "$red8",
        errorBgSolid: "$red9",
        errorBgSolidHover: "$red10",
        errorText: "$red11",
        errorTextContrast: "$red12",

        dangerBase: "$red1",
        dangerBgSubtle: "$red2",
        dangerBg: "$red3",
        dangerBgHover: "$red4",
        dangerBgActive: "$red5",
        dangerLine: "$red6",
        dangerBorder: "$red7",
        dangerBorderHover: "$red8",
        dangerBgSolid: "$red9",
        dangerBgSolidHover: "$red10",
        dangerText: "$red11",
        dangerTextContrast: "$red12",
        onDanger: "#fff",

        infoBase: "$blue1",
        infoBgSubtle: "$blue2",
        infoBg: "$blue3",
        infoBgHover: "$blue4",
        infoBgActive: "$blue5",
        infoLine: "$blue6",
        infoBorder: "$blue7",
        infoBorderHover: "$blue8",
        infoBgSolid: "$blue9",
        infoBgSolidHover: "$blue10",
        infoText: "$blue11",
        infoTextContrast: "$blue12",

        successBase: "$green1",
        successBgSubtle: "$green2",
        successBg: "$green3",
        successBgHover: "$green4",
        successBgActive: "$green5",
        successLine: "$green6",
        successBorder: "$green7",
        successBorderHover: "$green8",
        successBgSolid: "$green9",
        successBgSolidHover: "$green10",
        successText: "$green11",
        successTextContrast: "$green12",

        warningBase: "$orange1",
        warningBgSubtle: "$orange2",
        warningBg: "$orange3",
        warningBgHover: "$orange4",
        warningBgActive: "$orange5",
        warningLine: "$orange6",
        warningBorder: "$orange7",
        warningBorderHover: "$orange8",
        warningBgSolid: "$orange9",
        warningBgSolidHover: "$orange10",
        warningText: "$orange11",
        warningTextContrast: "$orange12",
        onWarning: "#fff",
      },
      fonts: {
        default: "InterVariable, sans-serif",
        mono: "'Fira CodeVariable', Consolas, Monaco, Andale Mono, Ubuntu Mono, monospace",
      },
      fontSizes: {
        xs: rem(12),
        sm: rem(14),
        base: rem(16),
        lg: rem(18),
        xl: rem(20),
        "2xl": rem(24),
        "3xl": rem(30),
        "4xl": rem(36),
        "5xl": rem(48),
        "6xl": rem(60),
        "7xl": rem(72),
        "8xl": rem(96),
        "9xl": rem(144),
      },
      fontWeights: {
        light: 300,
        normal: 350,
        medium: 600,
        semibold: 750,
        bold: 800,
      },
      radii: {
        1: "3px",
        2: "6px",
        3: "8px",
        4: "12px",
        full: "9999px",
      },
      sizes: {
        narrowContent: "40rem",
      },
      space: {
        1: rem(4),
        2: rem(8),
        3: rem(12),
        4: rem(16),
        5: rem(20),
        6: rem(24),
        7: rem(28),
        8: rem(32),
        9: rem(36),
        10: rem(40),
        11: rem(44),
        12: rem(48),
        13: rem(52),
        14: rem(56),
        15: rem(60),
        16: rem(64),
        17: rem(68),
        18: rem(72),
        19: rem(76),
        20: rem(80),
        21: rem(84),
        22: rem(88),
        23: rem(92),
        24: rem(96),
        25: rem(100),
        26: rem(104),
        27: rem(108),
        28: rem(112),
        29: rem(116),
        30: rem(120),
        31: rem(124),
        32: rem(128),
      },
      transitions: {
        duration1: "100ms",
        duration2: "150ms",
        duration3: "200ms",
        duration4: "300ms",
        duration5: "400ms",
      },
      shadows: {
        1: "0 1px 2px 0 rgba(0, 0, 0, 0.05);",
        2: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        3: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        4: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        5: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        6: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      },
    },
    media: {
      small: "(min-width: 640px)",
      medium: "(min-width: 860px)",
      large: "(min-width: 1100px)",
      xlarge: "(min-width: 1300px)",
    },
    utils: {
      applyReset: (value: "btn" | "input") => {
        if (value === "btn")
          return {
            margin: "0",
            padding: "0",
            border: 0,
            background: "transparent",
            fontFamily: "inherit",
            fontSize: "inherit",
            cursor: "pointer",
            lineHeight: 1,
            appearance: "none",
            textAlign: "start",
          };

        return {
          width: "100%",
          margin: "0",
          padding: "0",
          border: "1px solid $neutralBorder",
          background: "transparent",
          fontFamily: "inherit",
          fontSize: "inherit",
          lineHeight: 1,
          appearance: "none",
        };
      },
    },
  });
